import React, { useEffect, useState } from "react";
import { authFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Printer } from "lucide-react";

/**
 * Un QR con sotto il pulsante per scaricarlo e quello per stamparlo.
 *
 * L'immagine si scarica come blob invece di puntarci con un collegamento:
 * il frontend e il backend stanno su domini diversi, e in quel caso il
 * browser ignora la richiesta di scaricare e apre l'immagine in una scheda,
 * lasciando l'utente a fare "salva con nome" a mano.
 */
export default function QRBox({ percorso, titolo, sottotitolo, indirizzo, nomeFile }) {
  const [src, setSrc] = useState(null);
  const [errore, setErrore] = useState(false);

  useEffect(() => {
    let vivo = true;
    let url = null;
    authFetch(percorso)
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(b => { if (vivo) { url = URL.createObjectURL(b); setSrc(url); } })
      .catch(() => { if (vivo) setErrore(true); });
    return () => { vivo = false; if (url) URL.revokeObjectURL(url); };
  }, [percorso]);

  const scarica = () => {
    if (!src) return;
    const a = document.createElement("a");
    a.href = src;
    a.download = `${(nomeFile || titolo || "qr").replace(/[^\w\-]+/g, "_")}.png`;
    a.click();
  };

  const stampa = () => {
    if (!src) return;
    const f = window.open("", "_blank", "width=420,height=560");
    if (!f) { toast.error("Il browser ha bloccato la finestra di stampa"); return; }
    f.document.write(`<html><head><title>${titolo || "QR"}</title>
      <style>
        body { font-family: system-ui, sans-serif; text-align: center; padding: 24px; }
        img { width: 260px; height: 260px; }
        h1 { font-size: 15px; margin: 12px 0 2px; }
        p { font-size: 11px; color: #555; margin: 0; }
      </style></head><body>
      <img src="${src}" alt="QR" />
      <h1>${(titolo || "").replace(/</g, "&lt;")}</h1>
      <p>${(sottotitolo || "").replace(/</g, "&lt;")}</p>
      </body></html>`);
    f.document.close();
    // Si stampa quando l'immagine e' davvero a video, altrimenti esce bianca.
    f.onload = () => { f.focus(); f.print(); };
    setTimeout(() => { try { f.focus(); f.print(); } catch { /* gia' stampata */ } }, 400);
  };

  return (
    <div className="flex flex-col items-center gap-3" data-testid="qr-box">
      {errore
        ? <div className="w-64 h-64 border border-slate-200 rounded flex items-center justify-center text-sm text-slate-400">
            QR non disponibile
          </div>
        : <img src={src || undefined} alt="QR"
               className="w-64 h-64 border border-slate-200 rounded bg-white" />}

      {titolo && <div className="font-semibold text-sm text-center">{titolo}</div>}
      {indirizzo && <div className="text-xs text-slate-500 break-all">{indirizzo}</div>}

      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={scarica} disabled={!src}
                data-testid="qr-scarica">
          <Download className="w-4 h-4 mr-1.5" /> Scarica
        </Button>
        <Button variant="outline" size="sm" onClick={stampa} disabled={!src}
                data-testid="qr-stampa">
          <Printer className="w-4 h-4 mr-1.5" /> Stampa
        </Button>
      </div>
    </div>
  );
}
